import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { ModalTitle } from '@components/modal-title/modal-title';
import { GoogleCalendarService } from '@services/google-calendar.service';
import { NotifyService } from '@intelishs-group/notify-service';
import { environment } from 'src/environments/environment';
import { toLocalISOString } from '@shared/utils/date.utils';
import { MEETING_LINK_REGEX } from '@shared/utils/meeting-link.validator';
import type { VEtapaProspectoDTO } from '@schemas/prospect';

export interface RescheduleData {
  fecha: Date;
  hora: Date;
  scomentario: string;
  sgoogleEventId: string | null;
  slink: string | null;
}

export type ScheduleModalMode = 'reagendar' | 'sesion-propuesta';

interface ModalModeConfig {
  title: string;
  icon: string;
  buttonLabel: string;
  eventSummaryPrefix: string;
  eventDescription: (comment: string, slink?: string | null) => string;
  successTitle: string;
  successMessage: string;
  errorFallback: string;
  calendarErrorMessage: string;
  commentPlaceholder: string;
}

const appendLink = (base: string, slink?: string | null): string =>
  slink ? `${base}\nEnlace: ${slink}` : base;

const MODE_CONFIG: Record<ScheduleModalMode, ModalModeConfig> = {
  reagendar: {
    title: 'Reagendar Cita',
    icon: 'calendar',
    buttonLabel: 'Reagendar',
    eventSummaryPrefix: 'Reagenda',
    eventDescription: (comment, slink) =>
      appendLink(`Reagenda de cita\nRazón: ${comment}`, slink),
    successTitle: 'Reagendado',
    successMessage: 'La cita ha sido reagendada correctamente',
    errorFallback: 'Error al reagendar',
    calendarErrorMessage: 'Se reagendó, pero no se pudo sincronizar con Google Calendar',
    commentPlaceholder: 'Escribe la razón de la reagenda...',
  },
  'sesion-propuesta': {
    title: 'Sesión de Propuesta',
    icon: 'team',
    buttonLabel: 'Agendar Sesión',
    eventSummaryPrefix: 'Sesión de propuesta',
    eventDescription: (comment, slink) =>
      appendLink(`Sesión de propuesta\nComentario: ${comment}`, slink),
    successTitle: 'Sesión agendada',
    successMessage: 'La sesión de propuesta ha sido agendada correctamente',
    errorFallback: 'Error al agendar la sesión',
    calendarErrorMessage: 'Se agendó la sesión, pero no se pudo sincronizar con Google Calendar',
    commentPlaceholder: 'Escribe un comentario para la sesión...',
  },
};

@Component({
  selector: 'app-reschedule-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    NzModalModule,
    NzFormModule,
    NzDatePickerModule,
    NzTimePickerModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzAlertModule,
    ModalTitle,
  ],
  templateUrl: './reschedule-modal.html',
  styleUrl: './reschedule-modal.css',
})
export class RescheduleModal implements OnChanges, OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private notifyService = inject(NotifyService);
  readonly googleCal = inject(GoogleCalendarService);

  private readonly destroy$ = new Subject<void>();

  @Input() visible = false;
  @Input() prospect: VEtapaProspectoDTO | null = null;
  @Input() googleEventId: string | null = null;
  @Input() mode: ScheduleModalMode = 'reagendar';

  get config(): ModalModeConfig {
    return MODE_CONFIG[this.mode];
  }
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() scheduled = new EventEmitter<RescheduleData>();

  saving = false;
  connectingGoogle = false;
  googleConnected = false;

  private readonly COMMENT_REGEX = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ0-9\s.,;:!?'"()-]+$/;

  form = this.fb.group({
    fecha: [null as Date | null, Validators.required],
    hora: [null as Date | null, Validators.required],
    scomentario: ['', [Validators.required, Validators.pattern(this.COMMENT_REGEX)]],
    slink: ['', [Validators.pattern(MEETING_LINK_REGEX)]],
  });

  ngOnInit(): void {
    this.googleCal.accessToken$.pipe(takeUntil(this.destroy$)).subscribe((token) => {
      this.googleConnected = !!token;
      this.updateFormsState();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.form.reset();
      this.updateFormsState();
    }
  }

  private updateFormsState(): void {
    if (this.googleConnected) {
      this.form.enable();
    } else {
      this.form.disable();
    }
  }

  disabledDate = (current: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(current);
    d.setHours(0, 0, 0, 0);
    return d.getTime() < today.getTime();
  };

  disabledHours = (): number[] => {
    if (!this.isFechaToday()) return [];
    const now = new Date();
    let minHour = now.getHours();
    if (Math.ceil(now.getMinutes() / 10) * 10 >= 60) minHour++;
    return Array.from({ length: minHour }, (_, i) => i);
  };

  disabledMinutes = (hour: number): number[] => {
    if (!this.isFechaToday()) return [];
    const now = new Date();
    if (hour > now.getHours()) return [];
    if (hour < now.getHours()) return Array.from({ length: 60 }, (_, i) => i);
    const rounded = Math.ceil(now.getMinutes() / 10) * 10;
    if (rounded >= 60) return Array.from({ length: 60 }, (_, i) => i);
    return Array.from({ length: rounded }, (_, i) => i);
  };

  private isFechaToday(): boolean {
    const fecha = this.form.get('fecha')?.value;
    if (!fecha) return false;
    const today = new Date();
    return fecha.getFullYear() === today.getFullYear() &&
      fecha.getMonth() === today.getMonth() &&
      fecha.getDate() === today.getDate();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach(c => {
        c.markAsDirty();
        c.updateValueAndValidity({ onlySelf: true });
      });
      return;
    }

    this.saving = true;

    try {
      const val = this.form.getRawValue();
      const fecha = val.fecha!;
      const hora = val.hora!;
      fecha.setHours(hora.getHours(), hora.getMinutes(), 0, 0);
      const slink = (val.slink ?? '').trim() || null;

      // Google Calendar: update si existe, create si no
      const endDate = new Date(fecha.getTime() + 60 * 60 * 1000);
      const tz = this.googleCal.getUserTimeZone();
      const prospectName = this.prospect?.sprospecto ?? 'Prospecto';
      const calendarId = environment.googleCalendarId;
      const eventPayload = {
        summary: `${this.config.eventSummaryPrefix} — ${prospectName}`,
        description: this.config.eventDescription(val.scomentario!, slink),
        start: { dateTime: fecha.toISOString(), timeZone: tz },
        end: { dateTime: endDate.toISOString(), timeZone: tz },
      };

      let sgoogleEventId: string | null = null;
      const oldEventId = this.googleEventId;

      try {
        if (oldEventId) {
          try {
            const updated = await firstValueFrom(
              this.googleCal.updateEvent(calendarId, oldEventId, eventPayload)
            );
            sgoogleEventId = updated.id ?? oldEventId;
          } catch {
            // Fallback: evento eliminado externamente → crear nuevo
            const created = await firstValueFrom(
              this.googleCal.createEvent(calendarId, eventPayload)
            );
            sgoogleEventId = created.id ?? null;
          }
        } else {
          const created = await firstValueFrom(
            this.googleCal.createEvent(calendarId, eventPayload)
          );
          sgoogleEventId = created.id ?? null;
        }
      } catch {
        this.notifyService.errorNotyfy(this.config.calendarErrorMessage);
      }

      this.notifyService.successNotyfy(this.config.successTitle, this.config.successMessage);
      const internalScomentario = slink
        ? `${val.scomentario!}\nEnlace: ${slink}`
        : val.scomentario!;
      this.scheduled.emit({
        fecha: val.fecha!, hora: val.hora!,
        scomentario: internalScomentario,
        sgoogleEventId,
        slink,
      });
      this.close();
    } catch (error) {
      const msg = error instanceof Error ? error.message : this.config.errorFallback;
      this.notifyService.errorNotyfy(msg);
    } finally {
      this.saving = false;
    }
  }

  async connectGoogle(): Promise<void> {
    this.connectingGoogle = true;
    try {
      await this.googleCal.signIn();
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      const cancelled = msg.toLowerCase().includes('popup') || msg.toLowerCase().includes('cancel');
      if (!cancelled) {
        this.notifyService.errorNotyfy(msg || 'Error al conectar con Google');
      }
    } finally {
      this.connectingGoogle = false;
    }
  }
}
